<template>
  <div
    class="document">
    <helmet-provider>
      <helmet>
        <title v-if="loaded">{{prettyDocumentName}} - databin</title>
      </helmet>
    </helmet-provider>

    <el-dialog
      :title="$t('oops')"
      :visible.sync="overwriteDialog">
      <span>{{$t('overwrite_dialog_message')}}</span>
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleViewClick">{{$t('actions.view')}}</el-button>
        <el-button @click="handleOverwriteClick">{{$t('actions.overwrite')}}</el-button>
        <el-button @click="overwriteDialog = false">{{$t('actions.cancel')}}</el-button>
      </span>
    </el-dialog>

    <el-header class="nav">
      <router-link class="title" to="/">databin.me</router-link>
      <div class="spacer"/>
      <span class="readonly-message" v-if="loaded && certainVersion">{{$t('read_only')}}</span>
    </el-header>

    <el-header class="breadcrumbs-wrapper">
      <div class="breadcrumbs" v-if="loaded">
        <el-breadcrumb separator="/">
          <el-breadcrumb-item>{{data.project.name}}</el-breadcrumb-item>
          <el-breadcrumb-item :to="certainVersion ? '..' : ''">{{data.document.name}}</el-breadcrumb-item>
          <el-breadcrumb-item v-if="certainVersion">{{data.blob.sha.slice(0, 7)}}</el-breadcrumb-item>
        </el-breadcrumb>
        <div class="spacer"/>
        <span class="document-type">{{$t('type_text', {type: this.data.document.type})}}</span>
      </div>
    </el-header>

    <el-main class="codemirror-wrapper-wrapper">
      <div
        class="codemirror-wrapper"
        v-loading="loading">
        <codemirror
          v-if="loaded"
          class="codemirror"
          v-model="data.blob.content"
          :options="cmOptions"/>
      </div>
    </el-main>
    <footer class="footer-wrapper">
      <div class="footer">
        <el-input
          :placeholder="$t('search_placeholder')"
          v-model="comment"
          :disabled="!loaded || certainVersion"
          class="comment-input"/>

        <el-button
          type="primary"
          @click="saveHandler"
          :disabled="!loaded || certainVersion"
          :loading="saveLoading">
          {{$t('actions.save')}}
        </el-button>
      </div>
    </footer>
  </div>
</template>

<script>
import { Helmet, HelmetProvider } from '@jnields/vue-helmet';

import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';

//region codemirror imports
// languages
import 'codemirror/mode/javascript/javascript.js';
import 'codemirror/mode/yaml/yaml.js';
import 'codemirror/mode/toml/toml.js';

// require active-line.js
import 'codemirror/addon/selection/active-line.js';
// styleSelectedText
import 'codemirror/addon/selection/mark-selection.js';
import 'codemirror/addon/search/searchcursor.js';
// hint
import 'codemirror/addon/hint/show-hint.js';
import 'codemirror/addon/hint/show-hint.css';
import 'codemirror/addon/hint/javascript-hint.js';
import 'codemirror/addon/selection/active-line.js';
// highlightSelectionMatches
import 'codemirror/addon/scroll/annotatescrollbar.js';
import 'codemirror/addon/search/matchesonscrollbar.js';
import 'codemirror/addon/search/searchcursor.js';
import 'codemirror/addon/search/match-highlighter.js';
// keyMap
import 'codemirror/mode/clike/clike.js';
import 'codemirror/addon/edit/matchbrackets.js';
import 'codemirror/addon/comment/comment.js';
import 'codemirror/addon/dialog/dialog.js';
import 'codemirror/addon/dialog/dialog.css';
import 'codemirror/addon/search/searchcursor.js';
import 'codemirror/addon/search/search.js';
import 'codemirror/keymap/sublime.js';
// foldGutter
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/brace-fold.js';
import 'codemirror/addon/fold/comment-fold.js';
import 'codemirror/addon/fold/foldcode.js';
import 'codemirror/addon/fold/foldgutter.js';
import 'codemirror/addon/fold/indent-fold.js';
import 'codemirror/addon/fold/markdown-fold.js';
import 'codemirror/addon/fold/xml-fold.js';
// lint
import 'codemirror/addon/lint/lint.js';
import 'codemirror/addon/lint/json-lint.js';
import 'codemirror/addon/lint/yaml-lint.js';
//endregion

export default {
  name: 'document',
  components: {
    codemirror,
    Helmet,
    HelmetProvider,
  },
  props: {
    id: String,
    version: String,
  },
  data() {
    return {
      data: undefined,
      cmOption: {
        tabSize: 2,
        styleActiveLine: true,
        lineNumbers: true,
        line: true,
        extraKeys: {
          Tab: function betterTab(cm) {
            if (cm.somethingSelected()) {
              cm.indentSelection('add');
            } else {
              cm.replaceSelection(cm.getOption('indentWithTabs') ? '\t'
                : Array(cm.getOption('indentUnit') + 1).join(' '), 'end', '+input');
            }
          },
        },
        lineWrapping: true,
        autoCloseBrackets: true,
        theme: 'github-light',
        gutters: ['CodeMirror-lint-markers'],
        lint: true,
      },

      autosave: false,

      loading: true,
      saveLoading: false,

      overwriteDialog: false,

      latestVersionSha: '',

      comment: ''
    };
  },
  methods: {
    readDocument(id, version) {
      this.loading = true;
      return this.apiReadDocument(id, version)
        .then((res) => {
          //          console.log(res);
          const result = res.data.result;
          this.loading = false;
          this.data = result;
        })
        .catch((e) => {
          console.log(JSON.parse(JSON.stringify(e)));
          this.loading = false;

          if (!e.response) {
            return this.$alert(this.$t('e.network.message'), this.$t('e.network.title'));
          }

          const { data } = e.response;
          this.$alert(this.$t(`e.${data.error.code}.message`), this.$t(`e${data.error.code}.title`), {});
        });
    },
    createBlob(id, content, comment) {
      return this.apiCreateBlob(id, content, comment)
        .then((res) => {
          const { result } = res.data;
          this.saveLoading = false;

          this.data.blob.sha = result.sha;

          this.$message({
            message: this.$t('saved'),
            type: 'success',
          });
          return res.data;
        })
        .catch((e) => {
          this.saveLoading = false;

          if (!e.response) {
            this.$message({
              message: this.$t('save_failed_network_error'),
              type: 'error',
            });
            return {
              ok: false,
              error: {
                name: 'NETWORK_ERROR'
              }
            }
          }

          const { data } = e.response;
          console.log(data);
          this.saveErrorHandler(data.error);
          return e.response.data;
        });
    },
    async saveHandler() {
      this.saveLoading = true;

      const result = await this.apiGetDocumentVersion(this.id)
        .then(res => res.data)
        .catch(e =>
          e.response
            ? e.response.data
            : {
              ok: false,
              error: {name: "NETWORK_ERROR"},
            }
        )

      if (!result.ok) {
        return this.saveErrorHandler(result.data)
      }

      const { versions } = result.result;
      this.latestVersionSha = versions[0].sha;

      if (this.latestVersionSha !== this.data.blob.sha) {
        this.saveLoading = false;

        this.overwriteDialog = true;
        return console.log(versions)
      }

      return this.processCreateBlob()
    },
    async processCreateBlob() {
      const result = await this.createBlob(this.id, this.data.blob.content, this.comment)
      if (result.ok) this.comment = '';
      return result;
    },

    saveErrorHandler(error) {
      switch(error.name) {
        case 'NETWORK_ERROR':
          return this.$message({
            message: this.$t('save_failed_network_error'),
            type: 'error',
          });
        case 'PARSE_ERROR':
          return this.$message({
            message: this.$t('save_failed_parse_error'),
            type: 'error',
          });
        case 'SAME_CONTENT_ERROR':
          return this.$message({
            message: this.$t('save_failed_same_content_error'),
            type: 'warning',
          });
        case 'NOT_FOUND':
          return this.$message({
            message: this.$t('save_failed_not_found'),
            type: 'error',
          });
        default:
          return this.$alert(
            this.$t(`e.${data.error.code}.message`),
            this.$t(`e.${data.error.code}.title`),
            {}
          );
      }
    },

    handleKeydown(e) {
      if (e.code === 'KeyS' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.saveHandler();
      }
    },

    handleViewClick() {
      this.overwriteDialog = false;
      return window.open(`/document/${this.id}/version/${this.latestVersionSha}`, '_blank');
    },
    handleOverwriteClick() {
      this.overwriteDialog = false;
      return this.processCreateBlob()
    },

    apiReadDocument(id, version) {
      return this.axios.get(`${this.$store.state.apiUrl}/readDocument`, {
        params: {id, version},
      })
    },
    apiCreateBlob(id, content, comment) {
      return this.axios.post(`${this.$store.state.apiUrl}/createBlob`, {
        id, content, comment
      })
    },
    apiGetDocumentVersion(id) {
      return this.axios.get(`${this.$store.state.apiUrl}/getDocumentVersions`, {
        params: {id},
      })
    }
  },
  computed: {
    cmOptions() {
      return {
        ...this.cmOption,
        mode: this.mode,
        readOnly: this.certainVersion,
      };
    },
    mode() {
      switch (this.data.document.type) {
        case 'json': return { name: 'javascript', json: true };
        case 'yaml': return 'text/x-yaml';
        case 'toml': return 'text/x-toml';
        default: return 'raw';
      }
    },
    certainVersion() {
      return !!this.version
    },
    loaded() {
      return !!this.data
    },
    prettyDocumentName() {
      if (!this.loaded) return '';
      return `${this.data.document.name}`
    },
  },
  mounted() {
    window.jsonlint = require('jsonlint');
    window.jsyaml = require('js-yaml');

    this.readDocument(this.id, this.$route.params.version);

    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown);
  },
  beforeRouteUpdate(to, from, next) {
    this.readDocument(to.params.id, to.params.version)
      .then(next);
  },
};
</script>

<style scoped lang="less">
  @import '~codemirror/addon/lint/lint.css';
  @import '~codemirror-github-light/lib/codemirror-github-light-theme.css';

  @border-color: #ddd;
  @content-width: 960px;

  .flex-center() {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .document {
    min-height: 100vh;
    width: 100%;
    flex-grow: 1;

    display: grid;
    grid-template-rows: auto auto 1fr auto;
    grid-template-columns: 1fr;
    grid-template-areas:
      "nav"
      "breadcrumbs"
      "codemirror"
      "footer";
  }
  .nav {
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: solid 1px @border-color;
    background-color: #343a40;
    color: white;

    a {
      text-decoration: none;
      color: darken(white, 10%);
      &:visited {
        color: darken(white, 20%)
      }
      &:hover {
        color: white
      }
    }

    .title {
      font-size: 1.2rem;
    }
  }
  .breadcrumbs-wrapper {
    grid-area: breadcrumbs;

    width: 100%;
    padding: 0 25px;

    .breadcrumbs {
      display: flex;
      flex-direction: row;
      align-items: center;
      height: 100%;
      box-sizing: border-box;
      padding: 0 20px;
      margin: 0 auto;
      max-width: @content-width;

      .document-type {
        font-size: 14px;
        line-height: 1;
        color: #606266;
        /*font-weight: 600;*/
      }
    }
  }
  .codemirror-wrapper-wrapper {
    grid-area: codemirror;
    overflow: auto;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 0 25px 25px;

    .codemirror-wrapper {
      box-sizing: border-box;
      width: 100%;
      max-width: @content-width;
      height: 100%;
      margin: 0 auto;
    }
    .codemirror {
      padding: 25px 0;
      box-sizing: border-box;

      border: solid 1px @border-color;
      border-radius: 3px;

      height: 100%;
      display: flex;
      flex-direction: column;
      width: 100%;
    }
    .CodeMirror-scroll {
      height: 100%;
      overflow-y: auto;
      overflow-x: auto;
    }

  }
  .footer-wrapper {

    box-sizing: border-box;
    width: 100%;
    padding: 0 25px 50px;


    .footer {
      grid-area: footer;

      display: flex;
      flex-direction: column;
      align-items: flex-start;

      box-sizing: border-box;
      max-width: @content-width;
      padding: 20px;
      margin: 0 auto;

      border: solid 1px @border-color;
      /*background-color: #eee;*/
      border-radius: 5px;

      .comment-input {
        /*max-width: 500px;*/
        margin-bottom: 10px;
      }
    }
  }

  .spacer {
    flex-grow: 1;
  }
</style>

<style>
  .CodeMirror {
    height: 100%;
  }
</style>
